import React, { useState, useEffect, useCallback } from "react";
import {
  collection,
  onSnapshot,
  deleteDoc,
  doc,
  addDoc,
  query,
  where,
  limit,
  orderBy,
  startAfter,
  getDocs,
} from "firebase/firestore";
import { firestore, auth } from "../../../firebase";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import AddItemPopup from "./CrudItems";
import QRCodeDialog from "../QRCode/QRCodeDialog";
import SearchComponent from "./SearchComponent";
import CardView from "./CardView";
import ItemDrawer from "./ItemDrawer";

// --- MUI Imports ---
import {
  Paper,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Container,
  CircularProgress,
  Tooltip,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  FormControlLabel, // ✅ Added
  Divider,          // ✅ Added
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ComplaintIcon from "@mui/icons-material/ReportProblem";
import QrCodeIcon from "@mui/icons-material/QrCode";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
// --- END MUI Imports ---

function WarrantyList() {
  const [data, setData] = useState([]);
  const [view, setView] = useState("card");
  const [popupOpen, setPopupOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [qrOpen, setQrOpen] = useState(false);
  const [qrData, setQrData] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [openConfirmDeleteItem, setOpenConfirmDeleteItem] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [selectAll, setSelectAll] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [lastVisible, setLastVisible] = useState(null);
  const [userId, setUserId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const navigate = useNavigate();
  // const auth = getAuth(); // Already imported from firebase

  const parsePurchaseDate = useCallback((purchaseDate) => {
    if (!purchaseDate) return 0;
    const raw = String(purchaseDate).trim();
    const direct = new Date(raw);
    if (!Number.isNaN(direct.getTime())) {
      return direct.getTime();
    }

    const normalized = raw.replace(/-/g, " ");
    const fallback = new Date(normalized);
    if (!Number.isNaN(fallback.getTime())) {
      return fallback.getTime();
    }

    return 0;
  }, []);

  const sortItemsByPurchaseDateDesc = useCallback(
    (items) =>
      [...items].sort(
        (a, b) =>
          parsePurchaseDate(b.purchaseDate) - parsePurchaseDate(a.purchaseDate)
      ),
    [parsePurchaseDate]
  );

  const handleOpenDrawer = (id) => {
    const item = data.find((item) => item.id === id);
    setSelectedItem(item);
  };

  const handleCloseDrawer = () => {
    setSelectedItem(null);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
        setData([]);
        setLastVisible(null);
        navigate("/");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (userId) {
      setInitialLoading(true);
      const collectionRef = collection(firestore, "ItemMaster");
      const userRole = sessionStorage.getItem("userRole");
      const tenantId = sessionStorage.getItem("tenantId") || "";
      let q;

      if (userRole === "superadmin") {
        q = query(collectionRef, orderBy("purchaseDate", "desc"), limit(10));
      } else if (userRole === "admin") {
        if (!tenantId) {
          setData([]);
          setLastVisible(null);
          setInitialLoading(false);
          return () => {};
        }
        q = query(collectionRef, where("tenantId", "==", tenantId));
      } else {
        q = query(collectionRef, where("userId", "==", userId));
      }

      const unsubscribe = onSnapshot(
        q,
        (querySnapshot) => {
          const items = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          const sortedItems = sortItemsByPurchaseDateDesc(items);
          setData(sortedItems);

          if (userRole === "superadmin") {
            setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
          } else {
            setLastVisible(null);
          }
          setInitialLoading(false);
          setLoadingMore(false);
        },
        (error) => {
          console.error("Error fetching initial items:", error);
          setInitialLoading(false);
          setLoadingMore(false);
        }
      );

      return () => unsubscribe();
    } else {
      setData([]);
      setLastVisible(null);
      setInitialLoading(false);
    }
  }, [sortItemsByPurchaseDateDesc, userId]);

  const fetchMoreItems = async () => {
    if (loadingMore || !lastVisible || !userId) return;

    setLoadingMore(true);
    try {
      const collectionRef = collection(firestore, "ItemMaster");
      const userRole = sessionStorage.getItem("userRole");
      let q;

      if (userRole !== "superadmin") {
        setLoadingMore(false);
        return;
      }

      q = query(
        collectionRef,
        orderBy("purchaseDate", "desc"),
        startAfter(lastVisible),
        limit(10)
      );

      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const moreItems = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setData((prevData) => [...prevData, ...moreItems]);
        setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
      } else {
        setLastVisible(null);
      }
    } catch (error) {
      console.error("Error fetching more items:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedRows.length === 0) return;
    setOpenConfirmDelete(true);
  };

  const confirmDeleteSelected = async () => {
    try {
      const deletePromises = selectedRows.map((id) =>
        deleteDoc(doc(firestore, "ItemMaster", id))
      );
      await Promise.all(deletePromises);
      setSelectedRows([]);
      setSelectAll(false);
    } catch (error) {
      console.error("Error deleting selected documents: ", error);
    }
    setOpenConfirmDelete(false);
  };

  const cancelDeleteSelected = () => setOpenConfirmDelete(false);

  const handleDelete = (id) => {
    setItemToDelete(id);
    setOpenConfirmDeleteItem(true);
  };

  const confirmDeleteItem = async () => {
    if (itemToDelete) {
      try {
        await deleteDoc(doc(firestore, "ItemMaster", itemToDelete));
      } catch (error) {
        console.error(`Error deleting document with ID ${itemToDelete}: `, error);
      }
      setItemToDelete(null);
      setOpenConfirmDeleteItem(false);
    }
  };

  const cancelDeleteItem = () => {
    setOpenConfirmDeleteItem(false);
    setItemToDelete(null);
  };

  const handleEdit = (id) => navigate(`/EditForm/${id}`);
  const handleRaiseComplaint = (id) => navigate(`/raisecomplaint/${id}`);

  const handleAdd = async (newItem) => {
    if (!userId) {
      console.error("User ID is not available.");
      return;
    }
    const userRole = String(sessionStorage.getItem("userRole") || "")
      .trim()
      .toLowerCase();
    const sessionTenantId = sessionStorage.getItem("tenantId") || "";
    const resolvedTenantId =
      userRole === "superadmin"
        ? null
        : sessionTenantId || newItem.tenantId || newItem.facilityTenantId || "";

    if (userRole !== "superadmin" && !resolvedTenantId) {
      console.error("Tenant ID is not available for this user.");
      return;
    }

    try {
      await addDoc(collection(firestore, "ItemMaster"), {
        ...newItem,
        userId: userId,
        tenantId: resolvedTenantId,
      });
      setPopupOpen(false);
    } catch (error) {
      console.error("Error adding document: ", error);
    }
  };

  const handleSearchChange = (newSearchQuery) => {
    setSearchQuery(newSearchQuery);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  const handleGenerateQR = (row) => {
    const qrData = `${window.location.origin}/EditForm/${row.id}`;
    setQrData(qrData);
    setQrOpen(true);
  };

  const handleCloseQR = () => {
    setQrOpen(false);
    setQrData("");
  };

  const filteredData = data.filter(
    (item) =>
      !searchQuery ||
      item.itemMake?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.model?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectAll = (event) => {
    const checked = event.target.checked;
    if (checked) {
      setSelectedRows(filteredData.map((item) => item.id));
    } else {
      setSelectedRows([]);
    }
    setSelectAll(checked);
  };

   const handleRowSelect = (id) => {
     const newSelectedRows = selectedRows.includes(id)
       ? selectedRows.filter((rowId) => rowId !== id)
       : [...selectedRows, id];
     setSelectedRows(newSelectedRows);
     // Update selectAll state based on whether all *filtered* items are now selected
     setSelectAll(filteredData.length > 0 && newSelectedRows.length === filteredData.length);
   };


  return (
    <Box sx={{ flexGrow: 1, p: 3, bgcolor: "grey.100" }}>
      <Container maxWidth="xl">
        <Paper sx={{ p: { xs: 2, md: 3 }, boxShadow: 3 }}>
          {/* Top Bar: Search, View Toggle, Delete Button */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              gap: 2,
            }}
          >
            <SearchComponent
              searchQuery={searchQuery}
              setSearchQuery={handleSearchChange}
              onClearSearch={handleClearSearch}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}> {/* Added flexWrap */}
               <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAll}
                    onChange={handleSelectAll}
                    indeterminate={selectedRows.length > 0 && selectedRows.length < filteredData.length}
                    disabled={filteredData.length === 0}
                  />
                }
                label="Select All"
                sx={{ mr: 1 }}
              />
              <Tooltip title="List View">
                <IconButton
                  onClick={() => setView("grid")}
                  aria-label="List view"
                  color={view === "grid" ? "primary" : "default"}
                >
                  <ViewListIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Card View">
                <IconButton
                  onClick={() => setView("card")}
                  aria-label="Card view"
                  color={view === "card" ? "primary" : "default"}
                >
                  <GridViewIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteSelected}
                disabled={selectedRows.length === 0}
                startIcon={<DeleteIcon />}
                sx={{ ml: 1, minWidth: '160px' }}
              >
                Delete ({selectedRows.length})
              </Button>
              <Tooltip title="Add New Item">
                <IconButton
                  onClick={() => setPopupOpen(true)}
                  aria-label="add"
                  color="primary"
                  sx={{
                    background: "linear-gradient(to right, #6A5ACD, #FF69B4)",
                    color: "white",
                    ml: 1,
                    "&:hover": {
                      background: "linear-gradient(to right, #5a4bcd, #ef59a4)",
                    },
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Main Content Area */}
          {initialLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : filteredData.length === 0 ? (
             <Typography variant="h6" color="textSecondary" align="center" sx={{ p: 5 }}>
               {searchQuery ? "No items found matching your search." : "No items to display. Add items to get started."}
             </Typography>
          ) : view === "grid" ? (
            // --- Table View (Grid) ---
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                       {/* Checkbox moved to FormControlLabel */}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>Image</TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>Make</TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>Model</TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>Purchase Date</TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>Warranty Expiry</TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>Details</TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.map((row) => (
                    <TableRow hover key={row.id} selected={selectedRows.includes(row.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRows.includes(row.id)}
                          onChange={() => handleRowSelect(row.id)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          component="img"
                          src={row.imageUrl || "/placeholder-image.png"}
                          alt={row.itemMake || 'Item image'}
                          sx={{
                            height: 50,
                            width: 80,
                            objectFit: 'contain',
                            border: '1px solid #eee',
                            borderRadius: 1,
                            bgcolor: 'grey.100'
                          }}
                           onError={(e) => { e.target.onerror = null; e.target.src="/placeholder-image.png"}}
                        />
                      </TableCell>
                      <TableCell align="center">{row.itemMake || "N/A"}</TableCell>
                      <TableCell align="center">{row.model || "N/A"}</TableCell>
                      <TableCell align="center">{row.purchaseDate || "N/A"}</TableCell>
                      <TableCell align="center">{row.warrantyExpiryDate || "N/A"}</TableCell>
                      <TableCell align="center">
                         <Tooltip title="More Details">
                           <IconButton onClick={() => handleOpenDrawer(row.id)} size="small">
                             <UnfoldMoreIcon />
                           </IconButton>
                         </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          <Tooltip title="Edit">
                            <IconButton onClick={() => handleEdit(row.id)} size="small" color="primary">
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton onClick={() => handleDelete(row.id)} size="small" color="error">
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Raise Complaint">
                            <IconButton onClick={() => handleRaiseComplaint(row.id)} size="small" color="warning">
                              <ComplaintIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Generate QR Code">
                            <IconButton onClick={() => handleGenerateQR(row)} size="small">
                              <QrCodeIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
               {lastVisible && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                  <Button
                    variant="contained"
                    onClick={fetchMoreItems}
                    disabled={loadingMore}
                  >
                    {loadingMore ? <CircularProgress size={24} color="inherit" /> : "Load More"}
                  </Button>
                </Box>
              )}
            </TableContainer>
          ) : (
            // --- Card View ---
            <>
              <CardView
                items={filteredData}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onGenerateQR={handleGenerateQR}
                onRaiseComplaint={handleRaiseComplaint}
                selectedRows={selectedRows}
                setSelectedRows={handleRowSelect} // Pass the handler function
                onOpenDrawer={handleOpenDrawer}
              />
              {lastVisible && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                  <Button
                    variant="contained"
                    onClick={fetchMoreItems}
                    disabled={loadingMore}
                  >
                    {loadingMore ? <CircularProgress size={24} color="inherit" /> : "Load More"}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Container>

      {/* Item Drawer */}
      <ItemDrawer
        open={!!selectedItem}
        onClose={handleCloseDrawer}
        item={selectedItem}
        onEdit={handleEdit}
        onRaiseComplaint={handleRaiseComplaint}
        onGenerateQRCode={handleGenerateQR}
      />

      {/* Add Item Popup Dialog */}
      <AddItemPopup
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        onSave={handleAdd}
      />

      {/* Confirmation Dialogs */}
      <Dialog open={openConfirmDelete} onClose={cancelDeleteSelected}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete {selectedRows.length} item(s)?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteSelected}>Cancel</Button>
          <Button onClick={confirmDeleteSelected} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openConfirmDeleteItem} onClose={cancelDeleteItem}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this item?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteItem}>Cancel</Button>
          <Button onClick={confirmDeleteItem} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <QRCodeDialog open={qrOpen} qrData={qrData} onClose={handleCloseQR} />
    </Box>
  );
}

export default WarrantyList;